import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
} from 'chart.js';
import 'chartjs-adapter-date-fns';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

// Field labels for display
const FIELD_LABELS = {
  hr: 'Hours (HR)',
  dia_in: 'Diameter (Dia In)',
  material_usd: 'Material USD',
  material_cad: 'Material CAD',
  labour_rate_cad: 'Labour Rate CAD',
  labour_rate_usd: 'Labour Rate USD',
  labour_cost_cad: 'Labour Cost CAD',
  labour_cost_usd: 'Labour Cost USD',
  dia_2: 'Diameter 2 (Dia 2)'
};

// Chart colors for different fields
const FIELD_COLORS = {
  hr: { border: 'rgb(75, 192, 192)', background: 'rgba(75, 192, 192, 0.2)' },
  dia_in: { border: 'rgb(255, 99, 132)', background: 'rgba(255, 99, 132, 0.2)' },
  material_usd: { border: 'rgb(54, 162, 235)', background: 'rgba(54, 162, 235, 0.2)' },
  material_cad: { border: 'rgb(255, 206, 86)', background: 'rgba(255, 206, 86, 0.2)' },
  labour_rate_cad: { border: 'rgb(153, 102, 255)', background: 'rgba(153, 102, 255, 0.2)' },
  labour_rate_usd: { border: 'rgb(255, 159, 64)', background: 'rgba(255, 159, 64, 0.2)' },
  labour_cost_cad: { border: 'rgb(46, 204, 113)', background: 'rgba(46, 204, 113, 0.2)' },
  labour_cost_usd: { border: 'rgb(231, 76, 60)', background: 'rgba(231, 76, 60, 0.2)' },
  dia_2: { border: 'rgb(52, 73, 94)', background: 'rgba(52, 73, 94, 0.2)' }
};

const MaterialIndexTrendsModal = ({ isOpen, onClose, recordId, recordInfo }) => {
  const { instance, accounts } = useMsal();
  const [trendsData, setTrendsData] = useState([]);
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('chart'); // 'chart' or 'history'
  const [selectedField, setSelectedField] = useState('hr'); // Default to Hours

  // Fetch trends data
  useEffect(() => {
    if (isOpen && recordId) {
      fetchTrendsData();
      fetchHistoryData();
    }
  }, [isOpen, recordId]);

  const fetchTrendsData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        `/ottaviano/trends/${recordId}`
      );

      if (response.data.success) {
        setTrendsData(response.data.data);
      } else {
        setError(response.data.message || 'Failed to fetch trends');
      }
    } catch (error) {
      console.error('Error fetching material index trends:', error);
      setError('Error fetching trends: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistoryData = async () => {
    try {
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        `/ottaviano/history/${recordId}?limit=50`
      );

      if (response.data.success) {
        setHistoryData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching material index history:', error);
    }
  };

  // Filter trends by selected field
  const filteredTrends = trendsData.filter(t => t.field_name === selectedField);

  // Prepare chart data
  const prepareChartData = () => {
    if (!filteredTrends || filteredTrends.length === 0) {
      return { labels: [], datasets: [] };
    }

    // Sort by date
    const sortedTrends = [...filteredTrends].sort((a, b) => new Date(a.changed_at) - new Date(b.changed_at));

    const labels = sortedTrends.map(trend => new Date(trend.changed_at));
    const values = sortedTrends.map(trend => parseFloat(trend.new_value) || 0);

    const colors = FIELD_COLORS[selectedField] || FIELD_COLORS.hr;

    return {
      labels,
      datasets: [
        {
          label: FIELD_LABELS[selectedField] || selectedField,
          data: values,
          borderColor: colors.border,
          backgroundColor: colors.background,
          borderWidth: 2,
          pointBackgroundColor: colors.border,
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 5,
          pointHoverRadius: 8,
          fill: true,
          tension: 0.1
        }
      ]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: `${FIELD_LABELS[selectedField] || selectedField} Changes - ${recordInfo?.uid || recordInfo?.part_number || `Record #${recordId}`}`,
        font: {
          size: 16,
          weight: 'bold'
        }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          title: function(context) {
            return new Date(context[0].parsed.x).toLocaleString();
          },
          label: function(context) {
            return `Value: ${context.parsed.y?.toFixed(2) || 0}`;
          },
          afterLabel: function(context) {
            const dataIndex = context.dataIndex;
            const sortedTrends = [...filteredTrends].sort((a, b) => new Date(a.changed_at) - new Date(b.changed_at));
            const trend = sortedTrends[dataIndex];
            if (trend) {
              return [
                `Changed by: ${trend.user_name || trend.user_email || 'Unknown'}`,
                `Previous: ${trend.old_value?.toFixed(2) || 0}`
              ];
            }
            return '';
          }
        }
      }
    },
    scales: {
      x: {
        type: 'time',
        time: {
          displayFormats: {
            day: 'MMM dd',
            hour: 'MMM dd, HH:mm'
          }
        },
        title: {
          display: true,
          text: 'Date & Time'
        }
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: FIELD_LABELS[selectedField] || selectedField
        }
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    }
  };

  // Get unique fields that have trends
  const availableFields = [...new Set(trendsData.map(t => t.field_name))];

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        width: '90%',
        maxWidth: '1000px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 24px',
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
              📊 Rate Changes History
            </h2>
            {recordInfo && (
              <div style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>
                {recordInfo.uid && <span><strong>UID:</strong> {recordInfo.uid} | </span>}
                {recordInfo.part_number && <span><strong>Part #:</strong> {recordInfo.part_number} | </span>}
                {recordInfo.category && <span><strong>Category:</strong> {recordInfo.category}</span>}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
              padding: '4px 8px'
            }}
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div style={{ 
          display: 'flex', 
          borderBottom: '1px solid #e0e0e0',
          padding: '0 24px'
        }}>
          <button
            onClick={() => setActiveTab('chart')}
            style={{
              padding: '12px 20px',
              border: 'none',
              background: activeTab === 'chart' ? '#fff' : '#f5f5f5',
              borderBottom: activeTab === 'chart' ? '2px solid #0d6efd' : '2px solid transparent',
              cursor: 'pointer',
              fontWeight: activeTab === 'chart' ? 600 : 400,
              color: activeTab === 'chart' ? '#0d6efd' : '#666'
            }}
          >
            📈 Trend Chart
          </button>
          <button
            onClick={() => setActiveTab('history')}
            style={{
              padding: '12px 20px',
              border: 'none',
              background: activeTab === 'history' ? '#fff' : '#f5f5f5',
              borderBottom: activeTab === 'history' ? '2px solid #0d6efd' : '2px solid transparent',
              cursor: 'pointer',
              fontWeight: activeTab === 'history' ? 600 : 400,
              color: activeTab === 'history' ? '#0d6efd' : '#666'
            }}
          >
            📋 Change Log ({historyData.length})
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px', flex: 1, overflow: 'auto' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div style={{ fontSize: '24px', marginBottom: '12px' }}>⏳</div>
              <div>Loading trends data...</div>
            </div>
          ) : error ? (
            <div style={{ 
              backgroundColor: '#fee2e2', 
              color: '#dc2626', 
              padding: '16px', 
              borderRadius: '8px',
              textAlign: 'center' 
            }}>
              {error}
            </div>
          ) : activeTab === 'chart' ? (
            <div>
              {/* Field selector */}
              <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <label style={{ fontWeight: 500, fontSize: '14px' }}>Select Field:</label>
                {availableFields.length > 0 ? (
                  <select
                    value={selectedField}
                    onChange={(e) => setSelectedField(e.target.value)}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      fontSize: '14px'
                    }}
                  >
                    {availableFields.map(field => (
                      <option key={field} value={field}>
                        {FIELD_LABELS[field] || field} ({trendsData.filter(t => t.field_name === field).length} changes)
                      </option>
                    ))}
                  </select>
                ) : (
                  <span style={{ color: '#666', fontStyle: 'italic' }}>No rate changes recorded yet</span>
                )}
              </div>

              {/* Chart */}
              {filteredTrends.length > 0 ? (
                <div style={{ height: '400px' }}>
                  <Line data={prepareChartData()} options={chartOptions} />
                </div>
              ) : (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '60px 20px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  color: '#666'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
                  <div style={{ fontSize: '16px', fontWeight: 500 }}>No Changes Recorded Yet</div>
                  <div style={{ fontSize: '14px', marginTop: '8px' }}>
                    Rate changes (HR, labour rates, material costs, etc.) will appear here once edits are made.
                  </div>
                </div>
              )}

              {/* Changes Table */}
              {filteredTrends.length > 0 && (
                <div style={{ marginTop: '24px' }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '15px', fontWeight: 600 }}>
                    Change Details ({filteredTrends.length} records)
                  </h4>
                  <div style={{ maxHeight: '200px', overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0 }}>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Date</th>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Changed By</th>
                          <th style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e0e0e0' }}>Old Value</th>
                          <th style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e0e0e0' }}>New Value</th>
                          <th style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e0e0e0' }}>Change</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...filteredTrends].sort((a, b) => new Date(b.changed_at) - new Date(a.changed_at)).map((trend, idx) => {
                          const change = (parseFloat(trend.new_value) || 0) - (parseFloat(trend.old_value) || 0);
                          return (
                            <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                              <td style={{ padding: '8px 10px' }}>{new Date(trend.changed_at).toLocaleString()}</td>
                              <td style={{ padding: '8px 10px' }}>{trend.user_name || trend.user_email || 'Unknown'}</td>
                              <td style={{ padding: '8px 10px', textAlign: 'right' }}>{parseFloat(trend.old_value)?.toFixed(2) || '0.00'}</td>
                              <td style={{ padding: '8px 10px', textAlign: 'right' }}>{parseFloat(trend.new_value)?.toFixed(2) || '0.00'}</td>
                              <td style={{ 
                                padding: '8px 10px', 
                                textAlign: 'right',
                                color: change > 0 ? '#16a34a' : change < 0 ? '#dc2626' : '#666',
                                fontWeight: 500
                              }}>
                                {change > 0 ? '+' : ''}{change.toFixed(2)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* History Tab */
            <div>
              {historyData.length > 0 ? (
                <div style={{ maxHeight: '500px', overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0 }}>
                        <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Date</th>
                        <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Action</th>
                        <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Changed By</th>
                        <th style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e0e0e0' }}>HR</th>
                        <th style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e0e0e0' }}>Labour CAD</th>
                        <th style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e0e0e0' }}>Material CAD</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyData.map((entry, idx) => (
                        <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                          <td style={{ padding: '8px 10px' }}>{new Date(entry.changed_at).toLocaleString()}</td>
                          <td style={{ padding: '8px 10px' }}>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: 600,
                              backgroundColor: entry.action === 'INSERT' ? '#d1fae5' : entry.action === 'UPDATE' ? '#fef3c7' : '#fee2e2',
                              color: entry.action === 'INSERT' ? '#065f46' : entry.action === 'UPDATE' ? '#92400e' : '#991b1b'
                            }}>
                              {entry.action}
                            </span>
                          </td>
                          <td style={{ padding: '8px 10px' }}>{entry.changed_by || 'Unknown'}</td>
                          <td style={{ padding: '8px 10px', textAlign: 'right' }}>{parseFloat(entry.hr)?.toFixed(2) || '-'}</td>
                          <td style={{ padding: '8px 10px', textAlign: 'right' }}>${parseFloat(entry.labour_rate_cad)?.toFixed(2) || '-'}</td>
                          <td style={{ padding: '8px 10px', textAlign: 'right' }}>${parseFloat(entry.material_cad)?.toFixed(2) || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '60px 20px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  color: '#666'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
                  <div style={{ fontSize: '16px', fontWeight: 500 }}>No History Recorded Yet</div>
                  <div style={{ fontSize: '14px', marginTop: '8px' }}>
                    Full change history will appear here once edits are made.
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 24px',
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 20px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default MaterialIndexTrendsModal;

